import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useSettings } from '../context/SettingsContext';
import API_BASE_URL, { API_BASE_URL_WITHOUT_API } from '../config/api';
import './Settings.css';

const Settings = () => {
  const navigate = useNavigate();
  const { refreshSettings } = useSettings();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('company');
  
  const [companySettings, setCompanySettings] = useState({
    name: '',
    logo: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    copyright: ''
  });
  
  const [smsSettings, setSmsSettings] = useState({
    apiKey: '',
    senderId: '',
    dltSenderId: '',
    messageId: '',
    route: 'dlt',
    testPhoneNumber: '',
    testVariables: '',
    enabled: false
  });

  const [firebaseSettings, setFirebaseSettings] = useState({
    projectId: '',
    clientEmail: '',
    privateKeyId: '',
    clientId: '',
    privateKey: '',
    authUri: 'https://accounts.google.com/o/oauth2/auth',
    tokenUri: 'https://oauth2.googleapis.com/token',
    authProviderX509CertUrl: 'https://www.googleapis.com/oauth2/v1/certs',
    clientX509CertUrl: '',
    enabled: false
  });

  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [showGoogleClientSecret, setShowGoogleClientSecret] = useState(false);

  const [socialLoginSettings, setSocialLoginSettings] = useState({
    google: {
      enabled: false,
      clientIdWeb: '',
      clientIdIos: '',
      clientIdAndroid: '',
      clientSecret: ''
    },
    apple: {
      enabled: false
    }
  });

  const [apiConfigSettings, setApiConfigSettings] = useState({
    googleMapsApiKey: ''
  });

  const [appUpdateSettings, setAppUpdateSettings] = useState({
    enabled: true,
    forceUpdate: false,
    minAndroidVersion: '',
    minIosVersion: '',
    latestAndroidVersion: '',
    latestIosVersion: '',
    message: ''
  });
  const [staticOtpSettings, setStaticOtpSettings] = useState({
    mobileNumber: '',
    otp: '',
    enabled: false
  });

  const [logoPreview, setLogoPreview] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showStaticOtp, setShowStaticOtp] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/settings`);
      const settings = response.data.data;
      
      setCompanySettings({
        name: settings.company?.name || '',
        logo: settings.company?.logo || '',
        email: settings.company?.email || '',
        phone: settings.company?.phone || '',
        website: settings.company?.website || '',
        address: settings.company?.address || '',
        copyright: settings.company?.copyright || ''
      });
      
      setSmsSettings({
        apiKey: settings.sms?.apiKey || '',
        senderId: settings.sms?.senderId || '',
        dltSenderId: settings.sms?.dltSenderId || '',
        messageId: settings.sms?.messageId || '',
        route: settings.sms?.route || 'dlt',
        testPhoneNumber: settings.sms?.testPhoneNumber || '',
        testVariables: settings.sms?.testVariables || '',
        enabled: settings.sms?.enabled || false
      });

      setFirebaseSettings({
        projectId: settings.firebase?.projectId || '',
        clientEmail: settings.firebase?.clientEmail || '',
        privateKeyId: settings.firebase?.privateKeyId || '',
        clientId: settings.firebase?.clientId || '',
        privateKey: settings.firebase?.privateKey || '',
        authUri: settings.firebase?.authUri || 'https://accounts.google.com/o/oauth2/auth',
        tokenUri: settings.firebase?.tokenUri || 'https://oauth2.googleapis.com/token',
        authProviderX509CertUrl: settings.firebase?.authProviderX509CertUrl || 'https://www.googleapis.com/oauth2/v1/certs',
        clientX509CertUrl: settings.firebase?.clientX509CertUrl || '',
        enabled: settings.firebase?.enabled || false
      });

      setSocialLoginSettings({
        google: {
          enabled: settings.socialLogin?.google?.enabled || false,
          clientIdWeb: settings.socialLogin?.google?.clientIdWeb || '',
          clientIdIos: settings.socialLogin?.google?.clientIdIos || '',
          clientIdAndroid: settings.socialLogin?.google?.clientIdAndroid || '',
          clientSecret: settings.socialLogin?.google?.clientSecret || ''
        },
        apple: {
          enabled: settings.socialLogin?.apple?.enabled || false,
          clientId: settings.socialLogin?.apple?.clientId || '',
          teamId: settings.socialLogin?.apple?.teamId || '',
          keyId: settings.socialLogin?.apple?.keyId || '',
          bundleId: settings.socialLogin?.apple?.bundleId || '',
          privateKey: settings.socialLogin?.apple?.privateKey || ''
        }
      });

      setApiConfigSettings({
        googleMapsApiKey: settings.apiConfig?.googleMapsApiKey || ''
      });

      setStaticOtpSettings({
        mobileNumber: settings.staticOtp?.mobileNumber || '',
        otp: settings.staticOtp?.otp || '',
        enabled: settings.staticOtp?.enabled || false
      });
      setAppUpdateSettings({
        enabled: settings.appUpdate?.enabled !== false,
        forceUpdate: settings.appUpdate?.forceUpdate || false,
        minAndroidVersion: settings.appUpdate?.minAndroidVersion || '',
        minIosVersion: settings.appUpdate?.minIosVersion || '',
        latestAndroidVersion: settings.appUpdate?.latestAndroidVersion || '',
        latestIosVersion: settings.appUpdate?.latestIosVersion || '',
        message: settings.appUpdate?.message || ''
      });

      if (settings.company?.logo) {
        setLogoPreview(`${API_BASE_URL_WITHOUT_API}${settings.company.logo}`);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setError('Failed to load settings');
      setLoading(false);
    }
  };

  const handleCompanyChange = (e) => {
    const { name, value } = e.target;
    setCompanySettings({
      ...companySettings,
      [name]: value
    });
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/admin/settings/logo`);
      setLogoPreview('');
      setLogoFile(null);
      setCompanySettings({
        ...companySettings,
        logo: ''
      });
      setSuccess('Logo removed successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error removing logo:', error);
      setError('Failed to remove logo');
    }
  };

  const handleSmsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSmsSettings({
      ...smsSettings,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleFirebaseChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFirebaseSettings({
      ...firebaseSettings,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSocialLoginChange = (e) => {
    const { name, value, type, checked } = e.target;
    const [provider, field] = name.split('.');
    
    setSocialLoginSettings({
      ...socialLoginSettings,
      [provider]: {
        ...socialLoginSettings[provider],
        [field]: type === 'checkbox' ? checked : value
      }
    });
  };

  const handleApiConfigChange = (e) => {
    const { name, value } = e.target;
    setApiConfigSettings({
      ...apiConfigSettings,
      [name]: value
    });
  };

  const handleAppUpdateChange = (e) => {

    const { name, value, type, checked } = e.target;

    setAppUpdateSettings((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));

  };


  const handleStaticOtpChange = (e) => {
    const { name, value, type, checked } = e.target;
    setStaticOtpSettings({
      ...staticOtpSettings,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      
      // Company settings
      formData.append('companyName', companySettings.name);
      formData.append('companyEmail', companySettings.email);
      formData.append('companyPhone', companySettings.phone);
      formData.append('companyWebsite', companySettings.website);
      formData.append('companyAddress', companySettings.address);
      formData.append('companyCopyright', companySettings.copyright || '');
      if (logoFile) {
        formData.append('logo', logoFile);
      }
      
      // SMS settings
      formData.append('smsApiKey', smsSettings.apiKey);
      formData.append('smsSenderId', smsSettings.senderId);
      formData.append('smsDltSenderId', smsSettings.dltSenderId);
      formData.append('smsMessageId', smsSettings.messageId);
      formData.append('smsRoute', smsSettings.route);
      formData.append('smsTestPhoneNumber', smsSettings.testPhoneNumber);
      formData.append('smsTestVariables', smsSettings.testVariables);
      formData.append('smsEnabled', smsSettings.enabled);
      
      // Firebase settings
      formData.append('firebaseProjectId', firebaseSettings.projectId);
      formData.append('firebaseClientEmail', firebaseSettings.clientEmail);
      formData.append('firebasePrivateKeyId', firebaseSettings.privateKeyId);
      formData.append('firebaseClientId', firebaseSettings.clientId);
      formData.append('firebasePrivateKey', firebaseSettings.privateKey);
      formData.append('firebaseAuthUri', firebaseSettings.authUri);
      formData.append('firebaseTokenUri', firebaseSettings.tokenUri);
      formData.append('firebaseAuthProviderX509CertUrl', firebaseSettings.authProviderX509CertUrl);
      formData.append('firebaseClientX509CertUrl', firebaseSettings.clientX509CertUrl);
      formData.append('firebaseEnabled', firebaseSettings.enabled);

      // Social Login settings
      formData.append('googleEnabled', socialLoginSettings.google.enabled);
      formData.append('googleClientIdWeb', socialLoginSettings.google.clientIdWeb);
      formData.append('googleClientIdIos', socialLoginSettings.google.clientIdIos);
      formData.append('googleClientIdAndroid', socialLoginSettings.google.clientIdAndroid);
      formData.append('googleClientSecret', socialLoginSettings.google.clientSecret);
      
      formData.append('appleEnabled', socialLoginSettings.apple.enabled);

      // API Config settings
      formData.append('googleMapsApiKey', apiConfigSettings.googleMapsApiKey);

      // Static OTP settings
      formData.append('staticOtpMobileNumber', staticOtpSettings.mobileNumber);
      formData.append('staticOtpOtp', staticOtpSettings.otp);
      formData.append('staticOtpEnabled', staticOtpSettings.enabled);
      formData.append('appUpdateEnabled', appUpdateSettings.enabled);
      formData.append('appUpdateForce', appUpdateSettings.forceUpdate);
      formData.append('appUpdateMinAndroidVersion', appUpdateSettings.minAndroidVersion);
      formData.append('appUpdateMinIosVersion', appUpdateSettings.minIosVersion);
      formData.append('appUpdateLatestAndroidVersion', appUpdateSettings.latestAndroidVersion);
      formData.append('appUpdateLatestIosVersion', appUpdateSettings.latestIosVersion);
      formData.append('appUpdateMessage', appUpdateSettings.message);

      await axios.put(`${API_BASE_URL}/admin/settings`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
      setSaving(false);
      
      // Refresh settings context to update name and logo throughout the app
      refreshSettings();
      // Also refresh local settings to get updated logo path
      await fetchSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      setError(error.response?.data?.message || 'Failed to save settings');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container">
          <div className="settings-loading">Loading settings...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container">
        <div className="settings-header">
          <h1>Settings</h1>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <form onSubmit={handleSubmit} className="settings-form">
          {/* Tab Navigation */}
          <div className="settings-tabs">
            <button
              type="button"
              className={`settings-tab ${activeTab === 'company' ? 'active' : ''}`}
              onClick={() => setActiveTab('company')}
            >
              Company Settings
            </button>
            <button
              type="button"
              className={`settings-tab ${activeTab === 'sms' ? 'active' : ''}`}
              onClick={() => setActiveTab('sms')}
            >
              SMS Settings
            </button>
            <button
              type="button"
              className={`settings-tab ${activeTab === 'firebase' ? 'active' : ''}`}
              onClick={() => setActiveTab('firebase')}
            >
              Firebase Settings
            </button>
            <button
              type="button"
              className={`settings-tab ${activeTab === 'social-login' ? 'active' : ''}`}
              onClick={() => setActiveTab('social-login')}
            >
              Social Login
            </button>
            <button
              type="button"
              className={`settings-tab ${activeTab === 'api-config' ? 'active' : ''}`}
              onClick={() => setActiveTab('api-config')}
            >
              API Config
            </button>
            <button
              type="button"
              className={`settings-tab ${activeTab === 'static-otp' ? 'active' : ''}`}
              onClick={() => setActiveTab('static-otp')}
            >
              Static OTP
            </button>
            <button
              type="button"
              className={`settings-tab ${activeTab === 'app-update' ? 'active' : ''}`}
              onClick={() => setActiveTab('app-update')}
            >
              App Update
            </button>
          </div>

          {/* Tab Content */}
          <div className="settings-tab-content">
            {/* Company Settings Tab */}
            {activeTab === 'company' && (
              <div className="settings-section">
                <div className="section-header">
                  <h2>Company Settings</h2>
                  <p className="section-description">Configure your company information</p>
                </div>

                <div className="form-group">
                  <label>Company Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={companySettings.name}
                    onChange={handleCompanyChange}
                    required
                    placeholder="Enter company name"
                  />
                </div>

                <div className="form-group">
                  <label>Company Logo</label>
                  <div className="logo-upload-container">
                    {logoPreview && (
                      <div className="logo-preview">
                        <img src={logoPreview} alt="Company Logo" />
                        <button
                          type="button"
                          onClick={handleRemoveLogo}
                          className="btn btn-danger remove-logo-btn"
                        >
                          Remove Logo
                        </button>
                      </div>
                    )}
                    <div className="logo-upload">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        id="logo-upload"
                        style={{ display: 'none' }}
                      />
                      <label htmlFor="logo-upload" className="btn btn-secondary">
                        {logoPreview ? 'Change Logo' : 'Upload Logo'}
                      </label>
                      <span className="upload-hint">PNG, JPG, GIF, WebP or SVG (Max 2MB)</span>
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Company Email</label>
                    <input
                      type="email"
                      name="email"
                      value={companySettings.email}
                      onChange={handleCompanyChange}
                      placeholder="company@example.com"
                    />
                  </div>
                  <div className="form-group">
                    <label>Company Phone</label>
                    <input
                      type="text"
                      name="phone"
                      value={companySettings.phone}
                      onChange={handleCompanyChange}
                      placeholder="+1234567890"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Company Website</label>
                  <input
                    type="url"
                    name="website"
                    value={companySettings.website}
                    onChange={handleCompanyChange}
                    placeholder="https://www.example.com"
                  />
                </div>

                <div className="form-group">
                  <label>Company Address</label>
                  <textarea
                    name="address"
                    value={companySettings.address}
                    onChange={handleCompanyChange}
                    rows="3"
                    placeholder="Enter company address"
                  />
                </div>

                <div className="form-group">
                  <label>Copyright</label>
                  <input
                    type="text"
                    name="copyright"
                    value={companySettings.copyright}
                    onChange={handleCompanyChange}
                    placeholder="e.g. © 2025 Company Name. All rights reserved."
                  />
                  <span className="form-hint">Shown in the landing page footer. Leave empty to use default (company name + year).</span>
                </div>
              </div>
            )}

            {/* SMS Settings Tab */}
            {activeTab === 'sms' && (
              <div className="settings-section">
                <div className="section-header">
                  <h2>SMS Settings</h2>
                  <p className="section-description">
                    Configure Fast2SMS integration for sending SMS notifications.{' '}
                    <a 
                      href="https://docs.fast2sms.com/reference/new-endpoint" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="external-link"
                    >
                      View API Documentation
                    </a>
                  </p>
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      name="enabled"
                      checked={smsSettings.enabled}
                      onChange={handleSmsChange}
                      className="checkbox-input"
                    />
                    <span className="checkbox-label">Enable SMS Notifications</span>
                  </label>
                </div>

                <div className="form-group">
                  <label>Fast2SMS API Key *</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showApiKey ? "text" : "password"}
                      name="apiKey"
                      value={smsSettings.apiKey}
                      onChange={handleSmsChange}
                      placeholder="Enter your Fast2SMS API key"
                      required={smsSettings.enabled}
                      disabled={!smsSettings.enabled}
                      className="password-input"
                    />
                    {smsSettings.apiKey && (
                      <button
                        type="button"
                        onClick={() => setShowApiKey(!showApiKey)}
                        className="toggle-password-btn"
                        title={showApiKey ? "Hide" : "Show"}
                      >
                        {showApiKey ? "👁️" : "👁️‍🗨️"}
                      </button>
                    )}
                  </div>
                  <small className="form-hint">
                    Get your API key from{' '}
                    <a 
                      href="https://www.fast2sms.com/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="external-link"
                    >
                      Fast2SMS Dashboard
                    </a>
                  </small>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Sender ID *</label>
                    <input
                      type="text"
                      name="senderId"
                      value={smsSettings.senderId}
                      onChange={handleSmsChange}
                      placeholder="Enter sender ID"
                      required={smsSettings.enabled}
                      disabled={!smsSettings.enabled}
                      maxLength={6}
                    />
                    <small className="form-hint">
                      Sender ID (max 6 characters)
                    </small>
                  </div>
                  <div className="form-group">
                    <label>DLT Sender ID</label>
                    <input
                      type="text"
                      name="dltSenderId"
                      value={smsSettings.dltSenderId}
                      onChange={handleSmsChange}
                      placeholder="Your approved DLT sender ID"
                      disabled={!smsSettings.enabled}
                    />
                    <small className="form-hint">
                      Your approved DLT sender ID
                    </small>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Message ID (DLT Template)</label>
                    <input
                      type="text"
                      name="messageId"
                      value={smsSettings.messageId}
                      onChange={handleSmsChange}
                      placeholder="Enter DLT template message ID"
                      disabled={!smsSettings.enabled}
                    />
                    <small className="form-hint">
                      DLT Template Message ID
                    </small>
                  </div>
                  <div className="form-group">
                    <label>SMS Route</label>
                    <select
                      name="route"
                      value={smsSettings.route}
                      onChange={handleSmsChange}
                      disabled={!smsSettings.enabled}
                    >
                      <option value="dlt">DLT</option>
                      <option value="q">Q (Promotional)</option>
                      <option value="qt">QT (Transactional)</option>
                      <option value="t">T (Transactional)</option>
                      <option value="p">P (Promotional)</option>
                    </select>
                    <small className="form-hint">
                      SMS routing type
                    </small>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Test Phone Number</label>
                    <input
                      type="text"
                      name="testPhoneNumber"
                      value={smsSettings.testPhoneNumber}
                      onChange={handleSmsChange}
                      placeholder="e.g., +919876543210"
                      disabled={!smsSettings.enabled}
                    />
                    <small className="form-hint">
                      Phone number for testing SMS
                    </small>
                  </div>
                </div>

                <div className="form-group">
                  <label>Test Variables (DLT)</label>
                  <textarea
                    name="testVariables"
                    value={smsSettings.testVariables}
                    onChange={handleSmsChange}
                    rows="3"
                    placeholder='Enter test variables as JSON, e.g., {"var1": "value1", "var2": "value2"}'
                    disabled={!smsSettings.enabled}
                  />
                  <small className="form-hint">
                    Test variables for DLT template in JSON format
                  </small>
                </div>
              </div>
            )}

            {/* Firebase Settings Tab */}
            {activeTab === 'firebase' && (
              <div className="settings-section">
                <div className="section-header">
                  <h2>Firebase Settings</h2>
                  <p className="section-description">
                    Configure Firebase Cloud Messaging (FCM) for push notifications.{' '}
                    <a 
                      href="https://firebase.google.com/docs/cloud-messaging" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="external-link"
                    >
                      View Firebase Documentation
                    </a>
                    {' '}• Get service account JSON from{' '}
                    <a 
                      href="https://console.firebase.google.com/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="external-link"
                    >
                      Firebase Console
                    </a>
                  </p>
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      name="enabled"
                      checked={firebaseSettings.enabled}
                      onChange={handleFirebaseChange}
                      className="checkbox-input"
                    />
                    <span className="checkbox-label">Enable Push Notifications</span>
                  </label>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Project ID *</label>
                    <input
                      type="text"
                      name="projectId"
                      value={firebaseSettings.projectId}
                      onChange={handleFirebaseChange}
                      placeholder="e.g., astrosaptarishi-95bef"
                      required={firebaseSettings.enabled}
                      disabled={!firebaseSettings.enabled}
                    />
                    <small className="form-hint">
                      Firebase project ID from Google Cloud Console
                    </small>
                  </div>
                  <div className="form-group">
                    <label>Client Email *</label>
                    <input
                      type="email"
                      name="clientEmail"
                      value={firebaseSettings.clientEmail}
                      onChange={handleFirebaseChange}
                      placeholder="firebase-adminsdk-xxx@xxx.iam.gserviceaccount.com"
                      required={firebaseSettings.enabled}
                      disabled={!firebaseSettings.enabled}
                    />
                    <small className="form-hint">
                      Service account client email
                    </small>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Private Key ID *</label>
                    <input
                      type="text"
                      name="privateKeyId"
                      value={firebaseSettings.privateKeyId}
                      onChange={handleFirebaseChange}
                      placeholder="Enter private key ID"
                      required={firebaseSettings.enabled}
                      disabled={!firebaseSettings.enabled}
                    />
                    <small className="form-hint">
                      Private key ID from service account JSON
                    </small>
                  </div>
                  <div className="form-group">
                    <label>Client ID *</label>
                    <input
                      type="text"
                      name="clientId"
                      value={firebaseSettings.clientId}
                      onChange={handleFirebaseChange}
                      placeholder="Enter client ID"
                      required={firebaseSettings.enabled}
                      disabled={!firebaseSettings.enabled}
                    />
                    <small className="form-hint">
                      Client ID from service account JSON
                    </small>
                  </div>
                </div>

                <div className="form-group">
                  <label>Private Key *</label>
                  <div className="password-input-wrapper">
                    <textarea
                      type={showPrivateKey ? "text" : "password"}
                      name="privateKey"
                      value={firebaseSettings.privateKey}
                      onChange={handleFirebaseChange}
                      placeholder="-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----"
                      required={firebaseSettings.enabled}
                      disabled={!firebaseSettings.enabled}
                      rows="6"
                      className="private-key-input"
                    />
                    {firebaseSettings.privateKey && (
                      <button
                        type="button"
                        onClick={() => setShowPrivateKey(!showPrivateKey)}
                        className="toggle-password-btn"
                        title={showPrivateKey ? "Hide" : "Show"}
                        style={{ top: '12px', right: '12px' }}
                      >
                        {showPrivateKey ? "👁️" : "👁️‍🗨️"}
                      </button>
                    )}
                  </div>
                  <small className="form-hint">
                    Private key from service account JSON (including BEGIN and END markers)
                  </small>
                </div>

                <div className="form-group">
                  <label>Auth URI</label>
                  <input
                    type="url"
                    name="authUri"
                    value={firebaseSettings.authUri}
                    onChange={handleFirebaseChange}
                    placeholder="https://accounts.google.com/o/oauth2/auth"
                    disabled={!firebaseSettings.enabled}
                  />
                  <small className="form-hint">
                    OAuth 2.0 authorization endpoint (usually default)
                  </small>
                </div>

                <div className="form-group">
                  <label>Token URI</label>
                  <input
                    type="url"
                    name="tokenUri"
                    value={firebaseSettings.tokenUri}
                    onChange={handleFirebaseChange}
                    placeholder="https://oauth2.googleapis.com/token"
                    disabled={!firebaseSettings.enabled}
                  />
                  <small className="form-hint">
                    OAuth 2.0 token endpoint (usually default)
                  </small>
                </div>

                <div className="form-group">
                  <label>Auth Provider X509 Cert URL</label>
                  <input
                    type="url"
                    name="authProviderX509CertUrl"
                    value={firebaseSettings.authProviderX509CertUrl}
                    onChange={handleFirebaseChange}
                    placeholder="https://www.googleapis.com/oauth2/v1/certs"
                    disabled={!firebaseSettings.enabled}
                  />
                  <small className="form-hint">
                    OAuth 2.0 provider certificate URL (usually default)
                  </small>
                </div>

                <div className="form-group">
                  <label>Client X509 Cert URL</label>
                  <input
                    type="url"
                    name="clientX509CertUrl"
                    value={firebaseSettings.clientX509CertUrl}
                    onChange={handleFirebaseChange}
                    placeholder="https://www.googleapis.com/robot/v1/metadata/x509/..."
                    disabled={!firebaseSettings.enabled}
                  />
                  <small className="form-hint">
                    Client certificate URL from service account JSON
                  </small>
                </div>
              </div>
            )}

            {/* Social Login Settings Tab */}
            {activeTab === 'social-login' && (
              <div className="settings-section">
                <div className="section-header">
                  <h2>Social Login Configuration</h2>
                  <p className="section-description">
                    Configure Google and Apple Sign-In for mobile app authentication.{' '}
                    <a 
                      href="https://developers.google.com/identity/sign-in" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="external-link"
                    >
                      Google Sign-In Docs
                    </a>
                    {' '}•{' '}
                    <a 
                      href="https://developer.apple.com/sign-in-with-apple/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="external-link"
                    >
                      Apple Sign-In Docs
                    </a>
                  </p>
                </div>

                {/* Google Sign-In Configuration */}
                <div style={{ marginBottom: '40px', paddingBottom: '40px', borderBottom: '2px solid #e2e8f0' }}>
                  <h3 style={{ marginBottom: '20px', color: '#0f172a' }}>Google Sign-In</h3>
                  
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        name="google.enabled"
                        checked={socialLoginSettings.google.enabled}
                        onChange={handleSocialLoginChange}
                        className="checkbox-input"
                      />
                      <span className="checkbox-label">Enable Google Sign-In</span>
                    </label>
                  </div>

                  <div className="form-group">
                    <label>Web Client ID</label>
                    <input
                      type="text"
                      name="google.clientIdWeb"
                      value={socialLoginSettings.google.clientIdWeb}
                      onChange={handleSocialLoginChange}
                      placeholder="xxxxx.apps.googleusercontent.com"
                      disabled={!socialLoginSettings.google.enabled}
                    />
                    <small className="form-hint">
                      Google OAuth 2.0 Client ID for Web applications
                    </small>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>iOS Client ID</label>
                      <input
                        type="text"
                        name="google.clientIdIos"
                        value={socialLoginSettings.google.clientIdIos}
                        onChange={handleSocialLoginChange}
                        placeholder="xxxxx.apps.googleusercontent.com"
                        disabled={!socialLoginSettings.google.enabled}
                      />
                      <small className="form-hint">
                        Google OAuth 2.0 Client ID for iOS
                      </small>
                    </div>
                    <div className="form-group">
                      <label>Android Client ID</label>
                      <input
                        type="text"
                        name="google.clientIdAndroid"
                        value={socialLoginSettings.google.clientIdAndroid}
                        onChange={handleSocialLoginChange}
                        placeholder="xxxxx.apps.googleusercontent.com"
                        disabled={!socialLoginSettings.google.enabled}
                      />
                      <small className="form-hint">
                        Google OAuth 2.0 Client ID for Android
                      </small>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Client Secret (Optional)</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showGoogleClientSecret ? "text" : "password"}
                        name="google.clientSecret"
                        value={socialLoginSettings.google.clientSecret}
                        onChange={handleSocialLoginChange}
                        placeholder="Enter client secret"
                        disabled={!socialLoginSettings.google.enabled}
                        className="password-input"
                      />
                      {socialLoginSettings.google.clientSecret && (
                        <button
                          type="button"
                          onClick={() => setShowGoogleClientSecret(!showGoogleClientSecret)}
                          className="toggle-password-btn"
                          title={showGoogleClientSecret ? "Hide" : "Show"}
                        >
                          {showGoogleClientSecret ? "👁️" : "👁️‍🗨️"}
                        </button>
                      )}
                    </div>
                    <small className="form-hint">
                      Client secret for server-side token validation (optional)
                    </small>
                  </div>
                </div>

                {/* Apple Sign-In Configuration */}
                <div>
                  <h3 style={{ marginBottom: '20px', color: '#0f172a' }}>Apple Sign-In</h3>
                  
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        name="apple.enabled"
                        checked={socialLoginSettings.apple.enabled}
                        onChange={handleSocialLoginChange}
                        className="checkbox-input"
                      />
                      <span className="checkbox-label">Enable Apple Sign-In</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* API Config Tab */}
            {activeTab === 'api-config' && (
              <div className="settings-section">
                <div className="section-header">
                  <h2>API Configuration</h2>
                  <p className="section-description">
                    Configure third-party API keys for enhanced features.{' '}
                    <a 
                      href="https://developers.google.com/maps/documentation/places/web-service/get-api-key" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="external-link"
                    >
                      Get Google Maps API Key
                    </a>
                  </p>
                </div>

                <div className="form-group">
                  <label>Google Maps API Key</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showApiKey ? "text" : "password"}
                      name="googleMapsApiKey"
                      value={apiConfigSettings.googleMapsApiKey}
                      onChange={handleApiConfigChange}
                      placeholder="Enter your Google Maps API key"
                      className="password-input"
                    />
                    {apiConfigSettings.googleMapsApiKey && (
                      <button
                        type="button"
                        onClick={() => setShowApiKey(!showApiKey)}
                        className="toggle-password-btn"
                        title={showApiKey ? "Hide" : "Show"}
                      >
                        {showApiKey ? "👁️" : "👁️‍🗨️"}
                      </button>
                    )}
                  </div>
                  <small className="form-hint">
                    Required for place autocomplete and location services. Enable Places API and Geocoding API in Google Cloud Console.
                  </small>
                </div>
              </div>
            )}

            {/* Static OTP Tab */}
            {activeTab === 'app-update' && (
              <div className="settings-section">
                <div className="section-header">
                  <h2>Customer App Updates</h2>
                  <p className="section-description">
                    Controls the update prompt in the customer app. The app also checks Google Play and the App Store on its own;
                    these settings add a minimum version, let you force an update, and set the message customers see.
                    Leave a version blank to let the stores decide.
                  </p>
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      name="enabled"
                      checked={appUpdateSettings.enabled}
                      onChange={handleAppUpdateChange}
                      className="checkbox-input"
                    />
                    <span className="checkbox-label">Check for updates when the app opens</span>
                  </label>
                  <small className="form-hint">Switch off to stop the automatic prompt. "Check for updates" in the app's Profile still works.</small>
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      name="forceUpdate"
                      checked={appUpdateSettings.forceUpdate}
                      onChange={handleAppUpdateChange}
                      className="checkbox-input"
                    />
                    <span className="checkbox-label">Force every update</span>
                  </label>
                  <small className="form-hint">
                    Customers on any older version must update before using the app. Use the minimum versions below for a gentler rule.
                  </small>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Minimum Android version</label>
                    <input type="text" name="minAndroidVersion" value={appUpdateSettings.minAndroidVersion} onChange={handleAppUpdateChange} placeholder="e.g. 1.0.5" />
                    <small className="form-hint">Builds below this must update (Android shows a full-screen update).</small>
                  </div>
                  <div className="form-group">
                    <label>Minimum iOS version</label>
                    <input type="text" name="minIosVersion" value={appUpdateSettings.minIosVersion} onChange={handleAppUpdateChange} placeholder="e.g. 1.0.5" />
                    <small className="form-hint">Builds below this must update (iOS shows an update page that cannot be dismissed).</small>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Latest Android version (optional)</label>
                    <input type="text" name="latestAndroidVersion" value={appUpdateSettings.latestAndroidVersion} onChange={handleAppUpdateChange} placeholder="Leave blank to use Google Play" />
                  </div>
                  <div className="form-group">
                    <label>Latest iOS version (optional)</label>
                    <input type="text" name="latestIosVersion" value={appUpdateSettings.latestIosVersion} onChange={handleAppUpdateChange} placeholder="Leave blank to use the App Store" />
                  </div>
                </div>

                <div className="form-group">
                  <label>Message shown to customers (optional)</label>
                  <textarea
                    name="message"
                    value={appUpdateSettings.message}
                    onChange={handleAppUpdateChange}
                    rows={3}
                    placeholder="e.g. We have improved hotel search and fixed booking issues. Please update."
                  />
                  <small className="form-hint">Replaces the default "Version x.y.z is available" text in the update dialog.</small>
                </div>
              </div>
            )}

            {activeTab === 'static-otp' && (
              <div className="settings-section">
                <div className="section-header">
                  <h2>Static OTP Configuration</h2>
                  <p className="section-description">
                    Configure a static OTP for testing purposes. When enabled, the specified mobile number will always use the configured OTP for verification.
                  </p>
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      name="enabled"
                      checked={staticOtpSettings.enabled}
                      onChange={handleStaticOtpChange}
                      className="checkbox-input"
                    />
                    <span className="checkbox-label">Enable Static OTP</span>
                  </label>
                  <small className="form-hint">
                    When enabled, the configured mobile number will use the static OTP instead of generating a new one.
                  </small>
                </div>

                <div className="form-group">
                  <label>Mobile Number *</label>
                  <input
                    type="text"
                    name="mobileNumber"
                    value={staticOtpSettings.mobileNumber}
                    onChange={handleStaticOtpChange}
                    placeholder="Enter 10-digit mobile number (e.g., 9876543210)"
                    disabled={!staticOtpSettings.enabled}
                    required={staticOtpSettings.enabled}
                    pattern="[0-9]{10}"
                    maxLength="10"
                  />
                  <small className="form-hint">
                    The mobile number that will use the static OTP. Must be 10 digits.
                  </small>
                </div>

                <div className="form-group">
                  <label>Static OTP *</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showStaticOtp ? "text" : "password"}
                      name="otp"
                      value={staticOtpSettings.otp}
                      onChange={handleStaticOtpChange}
                      placeholder="Enter static OTP (e.g., 123456)"
                      disabled={!staticOtpSettings.enabled}
                      required={staticOtpSettings.enabled}
                      className="password-input"
                      maxLength="6"
                    />
                    {staticOtpSettings.otp && (
                      <button
                        type="button"
                        onClick={() => setShowStaticOtp(!showStaticOtp)}
                        className="toggle-password-btn"
                        title={showStaticOtp ? "Hide" : "Show"}
                      >
                        {showStaticOtp ? "👁️" : "👁️‍🗨️"}
                      </button>
                    )}
                  </div>
                  <small className="form-hint">
                    The OTP that will be used for the specified mobile number. Recommended: 6 digits.
                  </small>
                </div>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/sy-admin')}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default Settings;

